import React, { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Mapbox Components
import { HexagonMap, DriverMap, MapboxLocationPicker, NavigationOptions } from "@/components/MapboxMap";
import 'mapbox-gl/dist/mapbox-gl.css';

// Custom Components
import CookieBanner from "@/components/CookieBanner";
import TeenConsentForm from "@/components/TeenConsentForm";
import { TrustedContactsManager, PanicButton, DriverSelfieVerification } from "@/components/SafetyFeatures";
import { CancelRideDialog, DriverQuickActions, RiderPinDisplay, DriverOnWayNotification } from "@/components/RideActions";
import ParentDashboard from "@/components/ParentDashboard";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Shadcn UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

// Lucide Icons
import { 
  Car, MapPin, User, Phone, MessageCircle, Shield, Star, CreditCard, 
  DollarSign, Video, Clock, ChevronRight, Menu, X, LogOut, Settings,
  Navigation, AlertTriangle, CheckCircle, Send, Plus, Minus, Building2,
  Wallet, ArrowRight, UserPlus, Users, Eye, Heart, Zap, Home, History,
  PhoneCall, Mail, BanknoteIcon, CircleDollarSign, Timer, AlertCircle,
  FileText, Upload, Play, Car as CarIcon, ShieldCheck, Trash2, StickyNote,
  MapPinPlus, Route as RouteIcon, ChevronDown, ChevronUp, Compass, Map,
  Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("delevr_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("delevr_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("delevr_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("delevr_token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("delevr_token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = () => fetchUser();

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
const useApi = () => {
  const { token } = useAuth();
  
  const api = axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  return api;
};

// ============ COMPONENTS ============

// Logo Component - Uses uploaded logo image
const Logo = ({ size = "default" }) => {
  const sizeClasses = {
    small: "h-10",
    default: "h-14",
    large: "h-24"
  };
  
  return (
    <img 
      src="/logo.png" 
      alt="DELEVR - Getting You There" 
      className={`${sizeClasses[size]} object-contain`}
    />
  );
};

// Star Rating Component
const StarRating = ({ rating, onRate, readonly = false, size = "default" }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClasses = { small: "w-4 h-4", default: "w-6 h-6", large: "w-8 h-8" };

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} cursor-pointer transition-colors ${
            (hovered || rating) >= star ? "fill-yellow-400 text-yellow-400" : "text-slate-300"
          }`}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => !readonly && onRate && onRate(star)}
        />
      ))}
    </div>
  );
};

// Safety Shield FAB
const SafetyShield = ({ onEmergency, onCallOffice }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="fixed bottom-24 right-4 z-50 flex flex-col items-end gap-2">
      {expanded && (
        <>
          <Button
            data-testid="call-911-btn"
            onClick={() => { onEmergency(); window.open("tel:911"); }}
            className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-3 shadow-lg emergency-pulse"
          >
            <Phone className="w-5 h-5 mr-2" /> Call 911
          </Button>
          <Button
            data-testid="call-office-btn"
            onClick={() => { onCallOffice(); window.open("tel:8883363664"); }}
            className="bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full px-6 py-3 shadow-lg"
          >
            <Building2 className="w-5 h-5 mr-2" /> Call Office
          </Button>
        </>
      )}
      <Button
        data-testid="safety-shield-btn"
        onClick={() => setExpanded(!expanded)}
        className={`w-14 h-14 rounded-full shadow-lg transition-all ${
          expanded ? "bg-slate-700" : "bg-[#1E4DB7] hover:bg-[#163A8F]"
        }`}
      >
        <Shield className="w-6 h-6 text-white" />
      </Button>
    </div>
  );
};

// Video Recording Indicator
const RecordingIndicator = ({ active }) => {
  if (!active) return null;
  return (
    <div className="flex items-center gap-2 bg-slate-900/80 text-white px-3 py-1 rounded-full text-xs font-mono">
      <div className="w-2 h-2 bg-red-500 rounded-full rec-blink" />
      REC
    </div>
  );
};

// Location Picker Map - Now uses Mapbox
const LocationPicker = ({ onSelect, selectedLocation, label }) => {
  const [position, setPosition] = useState(selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : [40.7128, -74.0060]);
  const [address, setAddress] = useState(selectedLocation?.address || '');

  // Use Mapbox location picker if token is available
  if (MAPBOX_TOKEN) {
    return (
      <MapboxLocationPicker
        label={label}
        onSelect={onSelect}
        selectedLocation={selectedLocation}
        className="h-48"
      />
    );
  }

  // Fallback to Leaflet
  const MapClickHandler = () => {
    useMapEvents({
      click: async (e) => {
        const { lat, lng } = e.latlng;
        setPosition([lat, lng]);
        const addr = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        setAddress(addr);
        onSelect({ lat, lng, address: addr });
      }
    });
    return null;
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="h-48 rounded-xl overflow-hidden border border-slate-200">
        <MapContainer center={position} zoom={13} className="h-full w-full">
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <MapClickHandler />
          {position && <Marker position={position} />}
        </MapContainer>
      </div>
      {address && (
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#1E4DB7]" />
          {address}
        </div>
      )}
    </div>
  );
};

// ============ PAGES ============

// Landing Page
const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      if (user.role === "driver") {
        navigate(user.driver_status === "active" ? "/driver" : "/driver/onboarding");
      } else {
        navigate("/rider");
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Royal Blue Background */}
      <div className="bg-[#1E4DB7] relative overflow-hidden">
        {/* Header */}
        <header className="px-6 py-4 flex items-center justify-between max-w-7xl mx-auto relative z-10">
          <Logo />
          <div className="flex items-center gap-4">
            <a 
              href="tel:8883363664" 
              className="hidden sm:flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium"
            >
              <Phone className="w-4 h-4" />
              888.336.3664
            </a>
            <Button
              data-testid="login-btn"
              variant="ghost"
              onClick={() => navigate("/login")}
              className="text-white hover:bg-white/10"
            >
              Log In
            </Button>
            <Button
              data-testid="signup-btn"
              onClick={() => navigate("/register")}
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#1E4DB7] font-semibold rounded-full px-6"
            >
              Sign Up
            </Button>
          </div>
        </header>

        {/* Hero Content */}
        <main className="px-6 py-16 md:py-24 max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 px-4 py-1 rounded-full text-sm font-semibold">
                Safe • Reliable • Affordable
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight font-['Plus_Jakarta_Sans']">
                Getting You <span className="text-[#D4AF37]">There</span>
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Ride with confidence. DELEVR offers safe, affordable rides with features like emergency calling, 
                driver ratings, tips, multiple stops, and teen rider accounts.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="get-started-rider-btn"
                  onClick={() => navigate("/register?role=rider")}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#1E4DB7] rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  <Car className="w-5 h-5 mr-2" /> Book a Ride
                </Button>
                <Button
                  data-testid="get-started-driver-btn"
                  variant="outline"
                  onClick={() => navigate("/register?role=driver")}
                  className="border-2 border-white/50 text-white hover:bg-white hover:text-[#1E4DB7] rounded-full px-8 py-6 text-lg font-medium"
                >
                  <Wallet className="w-5 h-5 mr-2" /> Drive & Earn
                </Button>
              </div>
            </div>
            <div className="relative hidden md:block">
              <div className="absolute inset-0 bg-[#D4AF37]/20 rounded-3xl transform rotate-3" />
              <img
                src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600"
                alt="Rideshare"
                className="relative rounded-3xl shadow-2xl w-full object-cover border-4 border-[#D4AF37]/30"
              />
            </div>
          </div>
        </main>
        
        {/* Wave decoration */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L48 108C96 96 192 72 288 60C384 48 480 48 576 54C672 60 768 72 864 78C960 84 1056 84 1152 78C1248 72 1344 60 1392 54L1440 48V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Features Section */}
      <div className="px-6 py-16 max-w-7xl mx-auto">
        
        {/* WE PROMISE Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1E4DB7] mb-2">WE PROMISE</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Promise 1 - Recorded Rides */}
            <div className="flex gap-4 p-6 bg-gradient-to-r from-[#1E4DB7]/5 to-transparent rounded-2xl border-l-4 border-[#D4AF37]">
              <div className="w-12 h-12 bg-[#1E4DB7] rounded-xl flex items-center justify-center shrink-0">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Recorded & Monitored Rides</h3>
                <p className="text-sm text-slate-600">All rides will be recorded and monitored to ensure safety and security, including navigation.</p>
              </div>
            </div>

            {/* Promise 2 - W2W Program */}
            <div className="flex gap-4 p-6 bg-gradient-to-r from-[#1E4DB7]/5 to-transparent rounded-2xl border-l-4 border-[#D4AF37]">
              <div className="w-12 h-12 bg-[#1E4DB7] rounded-xl flex items-center justify-center shrink-0">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">W2W Program</h3>
                <p className="text-sm text-slate-600">Women drivers to service women riders to provide a safe space during rides.</p>
              </div>
            </div>

            {/* Promise 3 - Teen Riders */}
            <div className="flex gap-4 p-6 bg-gradient-to-r from-[#1E4DB7]/5 to-transparent rounded-2xl border-l-4 border-[#D4AF37]">
              <div className="w-12 h-12 bg-[#1E4DB7] rounded-xl flex items-center justify-center shrink-0">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Teen Rider Safety (13-17)</h3>
                <p className="text-sm text-slate-600">Accounts governed by parental consent and monitoring. Parents notified of every ride with location tracking. Selected drivers receive extensive background checks.</p>
              </div>
            </div>

            {/* Promise 4 - 50/50 Split */}
            <div className="flex gap-4 p-6 bg-gradient-to-r from-[#1E4DB7]/5 to-transparent rounded-2xl border-l-4 border-[#D4AF37]">
              <div className="w-12 h-12 bg-[#1E4DB7] rounded-xl flex items-center justify-center shrink-0">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-2">50/50 Driver Split</h3>
                <p className="text-sm text-slate-600">Drivers obtain a fair 50/50 split on all rides. We value our drivers.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { icon: Shield, title: "Safety First", desc: "Emergency 911 call & RAINN training" },
            { icon: Star, title: "Ratings", desc: "Rate drivers & passengers" },
            { icon: DollarSign, title: "Tip Tiers", desc: "Starting at $5 or customize" },
            { icon: RouteIcon, title: "Multiple Stops", desc: "Add stops to your ride" }
          ].map((feature, i) => (
            <Card key={i} className="bg-white border-slate-100 rounded-2xl shadow-sm hover:shadow-md hover:border-[#D4AF37] transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-[#1E4DB7]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-[#1E4DB7]" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-500">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* More Features */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Teen Rider Account", desc: "Safe rides for teens with parental tracking" },
            { icon: UserPlus, title: "Order for Others", desc: "Book rides for family or friends" },
            { icon: ShieldCheck, title: "Background Checks", desc: "All drivers verified & trained" }
          ].map((feature, i) => (
            <Card key={i} className="bg-gradient-to-br from-[#1E4DB7]/5 to-[#D4AF37]/5 border-0 rounded-2xl hover:shadow-md transition-all">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 bg-[#1E4DB7] rounded-xl flex items-center justify-center shrink-0">
                  <feature.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-slate-500">{feature.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1E4DB7] px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <Logo size="small" />
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span className="cursor-pointer hover:text-white transition-colors" onClick={() => navigate("/teen-consent")}>Teen Rider Info</span>
              <span className="text-white/40">|</span>
              <span className="cursor-pointer hover:text-white transition-colors">Terms & Conditions</span>
              <span className="text-white/40">|</span>
              <span className="cursor-pointer hover:text-white transition-colors">Privacy Policy</span>
              <span className="text-white/40">|</span>
              <span className="cursor-pointer hover:text-white transition-colors">Cookie Settings</span>
            </div>
          </div>
          <div className="text-center border-t border-white/20 pt-4">
            <p className="text-sm text-white/70">© 2025 DELEVR. Getting You There. | support@delevr.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "driver") {
        navigate(user.driver_status === "active" ? "/driver" : "/driver/onboarding");
      } else {
        navigate("/rider");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-2xl shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Welcome Back</CardTitle>
          <CardDescription>Sign in to continue to DELEVR</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                data-testid="login-password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl"
                required
              />
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full h-12 font-semibold"
              disabled={loading}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Separator />
          <p className="text-sm text-slate-500 text-center">
            Don't have an account?{" "}
            <span
              onClick={() => navigate("/register")}
              className="text-[#1E4DB7] font-semibold cursor-pointer hover:underline"
            >
              Sign Up
            </span>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: searchParams.get("role") || "rider",
    parent_email: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register(formData);
      toast.success(`Welcome to DELEVR, ${user.name}!`);
      if (user.role === "driver") {
        navigate("/driver/onboarding");
      } else {
        navigate("/rider");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-2xl shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <Logo />
          </div>
          <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Create Account</CardTitle>
          <CardDescription>Join DELEVR today</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="register-role-select" className="h-12 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rider">
                    <span className="flex items-center gap-2"><User className="w-4 h-4" /> Rider</span>
                  </SelectItem>
                  <SelectItem value="driver">
                    <span className="flex items-center gap-2"><Car className="w-4 h-4" /> Driver</span>
                  </SelectItem>
                  <SelectItem value="teen_rider">
                    <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Teen Rider</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                data-testid="register-name-input"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
                className="h-12 rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="register-email-input"
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="h-12 rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                data-testid="register-phone-input"
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="h-12 rounded-xl"
                required
              />
            </div>
            {formData.role === "teen_rider" && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="parent_email">Parent's Email</Label>
                  <Input
                    data-testid="register-parent-email-input"
                    id="parent_email"
                    type="email"
                    value={formData.parent_email}
                    onChange={(e) => setFormData({ ...formData, parent_email: e.target.value })}
                    placeholder="parent@example.com"
                    className="h-12 rounded-xl"
                    required
                  />
                  <p className="text-xs text-purple-600 flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Parent will be able to track rides
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-sm text-amber-800 font-medium mb-2">Parent Consent Required</p>
                  <p className="text-xs text-amber-700 mb-2">
                    Teen riders (ages 16-17) require a signed parent/guardian consent form.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/teen-consent")}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100 rounded-full"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Download Consent Form
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                data-testid="register-password-input"
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="h-12 rounded-xl"
                required
              />
            </div>
            <Button
              data-testid="register-submit-btn"
              type="submit"
              className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full h-12 font-semibold"
              disabled={loading}
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <Separator />
          <p className="text-sm text-slate-500 text-center">
            Already have an account?{" "}
            <span
              onClick={() => navigate("/login")}
              className="text-[#1E4DB7] font-semibold cursor-pointer hover:underline"
            >
              Sign In
            </span>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// Driver Onboarding Page
const DriverOnboardingPage = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const api = useApi();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState("vehicle");
  const [vehicleData, setVehicleData] = useState({
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: new Date().getFullYear(),
    vehicle_color: "",
    license_plate: ""
  });
  const [bgCheckData, setBgCheckData] = useState({
    ssn_last_four: "",
    date_of_birth: "",
    consent: false
  });
  const [uploading, setUploading] = useState(false);
  const [showRainnVideo, setShowRainnVideo] = useState(false);

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await api.get("/driver/onboarding/status");
      setStatus(response.data);
      if (response.data.can_drive) {
        navigate("/driver");
      }
    } catch (error) {
      console.error("Failed to fetch onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitVehicleInfo = async () => {
    // Validate year
    if (vehicleData.vehicle_year < 2013) {
      toast.error("Vehicle must be 2013 or newer");
      return;
    }
    setUploading(true);
    try {
      await api.post("/driver/onboarding/vehicle", vehicleData);
      toast.success("Vehicle info submitted!");
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit vehicle info");
    } finally {
      setUploading(false);
    }
  };

  const uploadDocument = async (docType, file) => {
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      await api.post(`/driver/onboarding/document/${docType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${docType.replace("_", " ")} uploaded!`);
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const submitBackgroundCheck = async () => {
    if (!bgCheckData.consent) {
      toast.error("Please consent to background check");
      return;
    }
    setUploading(true);
    try {
      await api.post("/driver/onboarding/background-check", bgCheckData);
      toast.success("Background check approved!");
      fetchOnboardingStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit background check");
    } finally {
      setUploading(false);
    }
  };

  const markRainnWatched = async () => {
    try {
      await api.post("/driver/onboarding/rainn-video-watched");
      toast.success("RAINN video completed!");
      setShowRainnVideo(false);
      fetchOnboardingStatus();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark video as watched");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E4DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const steps = [
    { id: "vehicle", title: "Vehicle Info", icon: CarIcon, completed: status?.steps?.vehicle_info },
    { id: "documents", title: "Documents", icon: FileText, completed: status?.steps?.drivers_license && status?.steps?.insurance && status?.steps?.registration },
    { id: "background", title: "Background Check", icon: ShieldCheck, completed: status?.steps?.background_check },
    { id: "rainn", title: "RAINN Training", icon: Video, completed: status?.steps?.rainn_video }
  ];

  return (
    <div data-testid="driver-onboarding" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Logo size="small" />
          <Button variant="ghost" size="icon" onClick={logout}>
            <LogOut className="w-4 h-4 text-slate-600" />
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-4xl mx-auto">
        <Card className="bg-white rounded-2xl shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="font-['Plus_Jakarta_Sans']">Driver Onboarding</CardTitle>
            <CardDescription>Complete all steps to start driving with DELEVR</CardDescription>
            <Progress value={status?.progress_percentage || 0} className="h-2 mt-4" />
            <p className="text-sm text-slate-500 mt-2">{status?.completed || 0} of {status?.total || 6} steps completed</p>
          </CardHeader>
        </Card>

        {/* Steps Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {steps.map((step) => (
            <Button
              key={step.id}
              variant={activeStep === step.id ? "default" : "outline"}
              onClick={() => setActiveStep(step.id)}
              className={`flex flex-col items-center gap-2 h-auto py-4 rounded-xl ${
                activeStep === step.id ? "bg-[#1E4DB7]" : step.completed ? "border-green-500 text-green-600" : ""
              }`}
            >
              {step.completed ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <step.icon className="w-5 h-5" />
              )}
              <span className="text-xs">{step.title}</span>
            </Button>
          ))}
        </div>

        {/* Step Content */}
        <Card className="bg-white rounded-2xl shadow-sm">
          <CardContent className="p-6">
            {activeStep === "vehicle" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CarIcon className="w-5 h-5 text-[#1E4DB7]" /> Vehicle Information
                </h3>
                <p className="text-sm text-slate-500">
                  Vehicles must be 2013 or newer to drive with DELEVR.
                </p>
                {status?.vehicle_info ? (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Vehicle Registered</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {status.vehicle_info.year} {status.vehicle_info.make} {status.vehicle_info.model} ({status.vehicle_info.color})
                    </p>
                    <p className="text-sm text-slate-500">Plate: {status.vehicle_info.license_plate}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Make</Label>
                        <Input
                          data-testid="vehicle-make-input"
                          value={vehicleData.vehicle_make}
                          onChange={(e) => setVehicleData({ ...vehicleData, vehicle_make: e.target.value })}
                          placeholder="Toyota"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Model</Label>
                        <Input
                          data-testid="vehicle-model-input"
                          value={vehicleData.vehicle_model}
                          onChange={(e) => setVehicleData({ ...vehicleData, vehicle_model: e.target.value })}
                          placeholder="Camry"
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Year (2013+)</Label>
                        <Input
                          data-testid="vehicle-year-input"
                          type="number"
                          min="2013"
                          max={new Date().getFullYear() + 1}
                          value={vehicleData.vehicle_year}
                          onChange={(e) => setVehicleData({ ...vehicleData, vehicle_year: parseInt(e.target.value) })}
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Color</Label>
                        <Input
                          data-testid="vehicle-color-input"
                          value={vehicleData.vehicle_color}
                          onChange={(e) => setVehicleData({ ...vehicleData, vehicle_color: e.target.value })}
                          placeholder="Silver"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>License Plate</Label>
                        <Input
                          data-testid="vehicle-plate-input"
                          value={vehicleData.license_plate}
                          onChange={(e) => setVehicleData({ ...vehicleData, license_plate: e.target.value })}
                          placeholder="ABC123"
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                    <Button
                      data-testid="submit-vehicle-btn"
                      onClick={submitVehicleInfo}
                      className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                      disabled={uploading}
                    >
                      {uploading ? "Submitting..." : "Submit Vehicle Info"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeStep === "documents" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1E4DB7]" /> Required Documents
                </h3>
                <p className="text-sm text-slate-500">
                  Upload your driver's license, insurance, and vehicle registration.
                </p>
                {[
                  { key: "drivers_license", label: "Driver's License" },
                  { key: "insurance", label: "Insurance Card" },
                  { key: "registration", label: "Vehicle Registration" }
                ].map((doc) => (
                  <div key={doc.key} className="flex items-center justify-between p-4 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      {status?.documents?.[doc.key] ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <Upload className="w-5 h-5 text-slate-400" />
                      )}
                      <span className="font-medium">{doc.label}</span>
                    </div>
                    {status?.documents?.[doc.key] ? (
                      <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => e.target.files?.[0] && uploadDocument(doc.key, e.target.files[0])}
                        />
                        <Button size="sm" variant="outline" className="rounded-full" asChild>
                          <span>Upload</span>
                        </Button>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}

            {activeStep === "background" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#1E4DB7]" /> Background Check
                </h3>
                {status?.steps?.background_check ? (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Background Check Approved</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">
                      We partner with trusted background check providers to ensure rider safety.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Last 4 of SSN</Label>
                        <Input
                          data-testid="ssn-input"
                          maxLength={4}
                          value={bgCheckData.ssn_last_four}
                          onChange={(e) => setBgCheckData({ ...bgCheckData, ssn_last_four: e.target.value })}
                          placeholder="1234"
                          className="rounded-xl"
                        />
                      </div>
                      <div>
                        <Label>Date of Birth</Label>
                        <Input
                          data-testid="dob-input"
                          type="date"
                          value={bgCheckData.date_of_birth}
                          onChange={(e) => setBgCheckData({ ...bgCheckData, date_of_birth: e.target.value })}
                          className="rounded-xl"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                      <Checkbox
                        data-testid="bg-check-consent"
                        checked={bgCheckData.consent}
                        onCheckedChange={(checked) => setBgCheckData({ ...bgCheckData, consent: checked })}
                      />
                      <span className="text-sm">I consent to a background check</span>
                    </div>
                    <Button
                      data-testid="submit-bg-check-btn"
                      onClick={submitBackgroundCheck}
                      className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                      disabled={uploading}
                    >
                      {uploading ? "Processing..." : "Submit Background Check"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {activeStep === "rainn" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Video className="w-5 h-5 text-[#1E4DB7]" /> RAINN Safety Training
                </h3>
                <p className="text-sm text-slate-500">
                  Watch this required safety awareness video before you can start driving.
                </p>
                {status?.steps?.rainn_video ? (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">RAINN Training Completed</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="aspect-video bg-slate-900 rounded-xl flex items-center justify-center relative overflow-hidden">
                      {showRainnVideo ? (
                        <iframe
                          width="100%"
                          height="100%"
                          src="https://www.youtube.com/embed/nqZyQrm_Mfc?autoplay=1"
                          title="RAINN Safety Video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <Button
                          data-testid="play-rainn-video-btn"
                          onClick={() => setShowRainnVideo(true)}
                          className="bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full px-8 py-6"
                        >
                          <Play className="w-6 h-6 mr-2" /> Watch Video
                        </Button>
                      )}
                    </div>
                    {showRainnVideo && (
                      <Button
                        data-testid="complete-rainn-btn"
                        onClick={markRainnWatched}
                        className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" /> Mark as Completed
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {status?.can_drive && (
          <Button
            onClick={() => navigate("/driver")}
            className="w-full mt-6 bg-green-500 hover:bg-green-600 text-white rounded-full h-12 font-semibold"
          >
            <CheckCircle className="w-5 h-5 mr-2" /> Start Driving
          </Button>
        )}
      </main>
    </div>
  );
};

// Rider Dashboard
const RiderDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const api = useApi();
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [stops, setStops] = useState([]);
  const [rideForOther, setRideForOther] = useState(false);
  const [otherRider, setOtherRider] = useState({ name: "", phone: "" });
  const [noteForDriver, setNoteForDriver] = useState("");
  const [loading, setLoading] = useState(false);
  const [showStops, setShowStops] = useState(false);
  const [vehicleType, setVehicleType] = useState("standard");
  const [passengerCount, setPassengerCount] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [scheduledTime, setScheduledTime] = useState("");  // For scheduled rides
  const [isScheduled, setIsScheduled] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelRideId, setCancelRideId] = useState(null);

  useEffect(() => {
    fetchRides();
    fetchVehicleTypes();
    const interval = setInterval(fetchRides, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchVehicleTypes = async () => {
    try {
      const response = await api.get("/vehicle-types");
      setVehicleTypes(response.data.vehicle_types);
    } catch (error) {
      console.error("Failed to fetch vehicle types:", error);
    }
  };

  const fetchRides = async () => {
    try {
      const response = await api.get("/rides");
      setRides(response.data);
      const active = response.data.find(r => ["pending", "accepted", "arrived", "in_progress"].includes(r.status));
      setActiveRide(active || null);
    } catch (error) {
      console.error("Failed to fetch rides:", error);
    }
  };

  const addStop = () => {
    setStops([...stops, { lat: 40.7128, lng: -74.0060, address: "", order: stops.length }]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStop = (index, location) => {
    const newStops = [...stops];
    newStops[index] = { ...location, order: index };
    setStops(newStops);
  };

  const bookRide = async () => {
    if (!pickup || !dropoff) {
      toast.error("Please select pickup and dropoff locations");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/rides", {
        pickup_location: pickup,
        dropoff_location: dropoff,
        stops: stops.filter(s => s.address),
        vehicle_type: vehicleType,
        passenger_count: passengerCount,
        ride_for_other: rideForOther,
        other_rider_name: rideForOther ? otherRider.name : null,
        other_rider_phone: rideForOther ? otherRider.phone : null,
        note_for_driver: noteForDriver || null,
        payment_method: paymentMethod,
        scheduled_time: isScheduled && scheduledTime ? new Date(scheduledTime).toISOString() : null
      });
      if (isScheduled) {
        toast.success("Ride scheduled successfully!");
      } else {
        toast.success("Ride booked! Looking for a driver...");
      }
      setShowBooking(false);
      if (!isScheduled) {
        setActiveRide(response.data);
      }
      setStops([]);
      setNoteForDriver("");
      setScheduledTime("");
      setIsScheduled(false);
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book ride");
    } finally {
      setLoading(false);
    }
  };

  const cancelRide = async (rideId) => {
    const ride = rides.find(r => r.id === rideId);
    const hasFee = ride && ["accepted", "arrived", "in_progress"].includes(ride.status);
    
    if (hasFee) {
      if (!window.confirm("Cancelling now will incur a $5.00 cancellation fee. Continue?")) {
        return;
      }
    }
    
    try {
      const response = await api.post(`/rides/${rideId}/cancel`, {});
      if (response.data.cancellation_fee > 0) {
        toast.warning(`Ride cancelled. Cancellation fee: $${response.data.cancellation_fee.toFixed(2)}`);
      } else {
        toast.success("Ride cancelled");
      }
      setActiveRide(null);
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel ride");
    }
  };

  return (
    <div data-testid="rider-dashboard" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo size="small" />
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/wallet")}
              className="text-slate-600"
            >
              <Wallet className="w-4 h-4 mr-2" /> Wallet
            </Button>
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#1E4DB7] text-white">
                {user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="w-4 h-4 text-slate-600" />
            </Button>
          </div>
        </div>
        
        {/* Parent Dashboard Link */}
        {user?.role !== "teen_rider" && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/parent')}
            className="text-[#1E4DB7] hover:bg-blue-50"
          >
            <Users className="w-4 h-4 mr-2" /> Parent Dashboard
          </Button>
        )}
      </header>

      {/* Teen Banner */}
      {user?.role === "teen_rider" && (
        <div className="bg-purple-50 border-b border-purple-100 px-6 py-2 text-center">
          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
            <Eye className="w-3 h-3 mr-1" /> Parent Tracking Active
          </Badge>
        </div>
      )}

      <main className="p-6 max-w-7xl mx-auto">
        {/* Active Ride or Book Ride */}
        {activeRide ? (
          <ActiveRideCard 
            ride={activeRide} 
            onCancel={() => cancelRide(activeRide.id)}
            onRefresh={fetchRides}
            isRider={true}
          />
        ) : (
          <Card className="bg-white rounded-2xl shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
                <Car className="w-5 h-5 text-[#1E4DB7]" /> Book a Ride
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showBooking ? (
                <Button
                  data-testid="start-booking-btn"
                  onClick={() => setShowBooking(true)}
                  className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full h-12 font-semibold"
                >
                  <Navigation className="w-5 h-5 mr-2" /> Where to?
                </Button>
              ) : (
                <div className="space-y-4">
                  {/* Vehicle Type Selection */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-[#1E4DB7]" />
                      Choose Your Ride
                    </Label>
                    <div className="grid grid-cols-1 gap-2">
                      {vehicleTypes.map((vt) => (
                        <div
                          key={vt.id}
                          data-testid={`vehicle-type-${vt.id}`}
                          onClick={() => setVehicleType(vt.id)}
                          className={`p-3 border rounded-xl cursor-pointer transition-all ${
                            vehicleType === vt.id 
                              ? "border-[#1E4DB7] bg-blue-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{vt.name}</p>
                              <p className="text-xs text-slate-500">{vt.description}</p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {vt.multiplier === 1 ? "Base" : `${vt.multiplier}x`}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Passenger Count */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#1E4DB7]" />
                      Passengers (Max 2)
                    </Label>
                    <div className="flex items-center gap-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengerCount(Math.max(1, passengerCount - 1))}
                        disabled={passengerCount <= 1}
                        className="rounded-full"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="text-2xl font-bold w-8 text-center">{passengerCount}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setPassengerCount(Math.min(2, passengerCount + 1))}
                        disabled={passengerCount >= 2}
                        className="rounded-full"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <LocationPicker 
                    label="Pickup Location" 
                    onSelect={setPickup}
                    selectedLocation={pickup}
                  />
                  
                  {/* Multiple Stops */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2">
                        <RouteIcon className="w-4 h-4 text-[#1E4DB7]" />
                        Add Stops (+$2 each)
                      </Label>
                      <Button
                        data-testid="toggle-stops-btn"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowStops(!showStops)}
                      >
                        {showStops ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                    {showStops && (
                      <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                        {stops.map((stop, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="flex-1">
                              <LocationPicker
                                label={`Stop ${index + 1}`}
                                onSelect={(loc) => updateStop(index, loc)}
                                selectedLocation={stop}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeStop(index)}
                              className="text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          data-testid="add-stop-btn"
                          variant="outline"
                          onClick={addStop}
                          className="w-full rounded-full"
                        >
                          <MapPinPlus className="w-4 h-4 mr-2" /> Add Stop
                        </Button>
                      </div>
                    )}
                  </div>

                  <LocationPicker 
                    label="Dropoff Location" 
                    onSelect={setDropoff}
                    selectedLocation={dropoff}
                  />
                  
                  {/* Note for Driver */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <StickyNote className="w-4 h-4 text-[#1E4DB7]" />
                      Note for Driver (optional)
                    </Label>
                    <Textarea
                      data-testid="note-for-driver-input"
                      value={noteForDriver}
                      onChange={(e) => setNoteForDriver(e.target.value)}
                      placeholder="E.g., I'll be at the front entrance, wearing a red jacket..."
                      className="rounded-xl"
                      rows={2}
                    />
                  </div>
                  
                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#1E4DB7]" />
                      Payment Method
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: "card", label: "Credit Card", icon: CreditCard },
                        { id: "apple_pay", label: "Apple Pay", icon: Wallet },
                        { id: "cashapp", label: "Cash App", icon: DollarSign }
                      ].map((pm) => (
                        <Button
                          key={pm.id}
                          data-testid={`payment-${pm.id}`}
                          variant={paymentMethod === pm.id ? "default" : "outline"}
                          onClick={() => setPaymentMethod(pm.id)}
                          className={`flex flex-col items-center gap-1 h-auto py-3 rounded-xl ${
                            paymentMethod === pm.id ? "bg-[#1E4DB7]" : ""
                          }`}
                        >
                          <pm.icon className="w-5 h-5" />
                          <span className="text-xs">{pm.label}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Order for someone else */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <UserPlus className="w-5 h-5 text-[#1E4DB7]" />
                      <span className="font-medium">Order for someone else</span>
                    </div>
                    <Switch
                      data-testid="ride-for-other-switch"
                      checked={rideForOther}
                      onCheckedChange={setRideForOther}
                    />
                  </div>
                  
                  {rideForOther && (
                    <div className="space-y-3 p-4 bg-blue-50 rounded-xl">
                      <Input
                        data-testid="other-rider-name-input"
                        placeholder="Rider's name"
                        value={otherRider.name}
                        onChange={(e) => setOtherRider({ ...otherRider, name: e.target.value })}
                        className="h-11 rounded-xl"
                      />
                      <Input
                        data-testid="other-rider-phone-input"
                        placeholder="Rider's phone"
                        value={otherRider.phone}
                        onChange={(e) => setOtherRider({ ...otherRider, phone: e.target.value })}
                        className="h-11 rounded-xl"
                      />
                    </div>
                  )}

                  {/* Schedule Ride Toggle */}
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-700">Schedule for later</span>
                    </div>
                    <Switch
                      data-testid="schedule-ride-switch"
                      checked={isScheduled}
                      onCheckedChange={setIsScheduled}
                    />
                  </div>
                  
                  {isScheduled && (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Select date and time</Label>
                      <Input
                        data-testid="scheduled-time-input"
                        type="datetime-local"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="h-12 rounded-xl"
                      />
                      {user?.role === "teen_rider" && (
                        <p className="text-xs text-purple-600">
                          Note: Teen riders cannot schedule rides between 11:30 PM - 5:00 AM
                        </p>
                      )}
                    </div>
                  )}

                  {/* Cancellation Policy Notice */}
                  <div className="p-3 bg-amber-50 rounded-xl text-xs text-amber-700 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>$5 cancellation fee applies after driver accepts. 3 min free wait, $5 fee after 4 min.</span>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setShowBooking(false); setStops([]); setIsScheduled(false); setScheduledTime(""); }}
                      className="flex-1 rounded-full"
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="confirm-booking-btn"
                      onClick={bookRide}
                      className="flex-1 bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                      disabled={loading || (isScheduled && !scheduledTime)}
                    >
                      {loading ? "Booking..." : isScheduled ? "Schedule Ride" : "Book Ride"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ride History */}
        <Card className="bg-white rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
              <History className="w-5 h-5 text-slate-400" /> Ride History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80">
              {rides.filter(r => r.status === "completed" || r.status === "cancelled").length === 0 ? (
                <p className="text-center text-slate-500 py-8">No ride history yet</p>
              ) : (
                <div className="space-y-3">
                  {rides.filter(r => r.status === "completed" || r.status === "cancelled").map((ride) => (
                    <RideHistoryItem key={ride.id} ride={ride} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Trusted Contacts */}
        <TrustedContactsManager 
          token={localStorage.getItem('token')}
          onUpdate={() => {}}
        />
      </main>

      <SafetyShield 
        onEmergency={() => toast.error("Calling 911...")}
        onCallOffice={() => toast.info("Calling DELEVR Office...")}
      />
    </div>
  );
};

// Active Ride Card
const ActiveRideCard = ({ ride, onCancel, onRefresh, isRider }) => {
  const api = useApi();
  const { user } = useAuth();
  const [showChat, setShowChat] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [waitTime, setWaitTime] = useState(null);

  const [driverLocation, setDriverLocation] = useState(null);
  const [showNavOptions, setShowNavOptions] = useState(false);

  useEffect(() => {
    if (ride.status === "arrived" && ride.driver_arrived_at) {
      fetchWaitTime();
      const interval = setInterval(fetchWaitTime, 5000);
      return () => clearInterval(interval);
    }
  }, [ride.status, ride.driver_arrived_at]);

  // Fetch driver location for real-time tracking (rider view)
  useEffect(() => {
    if (isRider && ride.driver_id && ["accepted", "arrived", "in_progress"].includes(ride.status)) {
      fetchDriverLocation();
      const interval = setInterval(fetchDriverLocation, 5000);
      return () => clearInterval(interval);
    }
  }, [ride.driver_id, ride.status, isRider]);

  const fetchDriverLocation = async () => {
    try {
      const response = await api.get(`/drivers/location/${ride.driver_id}`);
      setDriverLocation(response.data);
    } catch (error) {
      // Driver location not available yet
    }
  };

  const fetchWaitTime = async () => {
    try {
      const response = await api.get(`/rides/${ride.id}/wait-time`);
      setWaitTime(response.data);
    } catch (error) {
      console.error("Failed to fetch wait time:", error);
    }
  };

  const markArrived = async () => {
    try {
      await api.post(`/rides/${ride.id}/arrived`);
      toast.success("Marked as arrived! Wait timer started.");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark arrived");
    }
  };

  const startRide = async () => {
    try {
      await api.post(`/rides/${ride.id}/start`);
      toast.success("Ride started!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start ride");
    }
  };

  const nextStop = async () => {
    try {
      await api.post(`/rides/${ride.id}/next-stop`);
      toast.success("Proceeding to next stop!");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance stop");
    }
  };

  const completeRide = async () => {
    try {
      await api.post(`/rides/${ride.id}/complete`);
      toast.success("Ride completed!");
      setShowRating(true);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete ride");
    }
  };

  const cancelRide = async () => {
    try {
      const response = await api.post(`/rides/${ride.id}/cancel`, {});
      if (!isRider && waitTime?.can_cancel) {
        toast.success("Ride cancelled. Wait time charge earned.");
      } else {
        toast.success("Ride cancelled");
      }
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel ride");
    }
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    accepted: "bg-blue-100 text-blue-700",
    arrived: "bg-purple-100 text-purple-700",
    in_progress: "bg-green-100 text-green-700",
    completed: "bg-slate-100 text-slate-700"
  };

  const vehicleTypeNames = {
    standard: "Standard",
    comfort: "Comfort",
    black: "Black Car",
    black_suv: "Black SUV",
    limo: "Limousine"
  };

  // Build markers for the map
  const mapMarkers = [
    { lat: ride.pickup_location.lat, lng: ride.pickup_location.lng, type: 'pickup', popup: 'Pickup' },
    ...ride.stops?.map((s, i) => ({ lat: s.lat, lng: s.lng, type: 'stop', popup: `Stop ${i+1}` })) || [],
    { lat: ride.dropoff_location.lat, lng: ride.dropoff_location.lng, type: 'dropoff', popup: 'Dropoff' }
  ];

  // Add driver location if available
  if (driverLocation) {
    mapMarkers.push({
      lat: driverLocation.lat,
      lng: driverLocation.lng,
      type: 'driver',
      popup: ride.driver_name || 'Driver'
    });
  }

  return (
    <>
      <Card className="bg-white rounded-2xl shadow-sm mb-6 border-l-4 border-l-[#1E4DB7]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
              <Car className="w-5 h-5 text-[#1E4DB7]" /> Active Ride
            </CardTitle>
            <div className="flex items-center gap-2">
              {user?.video_subscription && <RecordingIndicator active={true} />}
              <Badge className={statusColors[ride.status]}>
                {ride.status.replace("_", " ").toUpperCase()}
              </Badge>
            </div>
          </div>
          {/* Vehicle & Passenger Info */}
          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <Car className="w-4 h-4" />
              {vehicleTypeNames[ride.vehicle_type] || "Standard"}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {ride.passenger_count || 1} passenger{(ride.passenger_count || 1) > 1 ? "s" : ""}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Wait Time Warning (for rider) */}
          {isRider && ride.status === "arrived" && waitTime && (
            <div className="p-3 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-orange-700">
                  <Timer className="w-4 h-4" />
                  <span className="font-medium">Driver waiting</span>
                </div>
                <span className="text-lg font-bold text-orange-700">
                  {Math.floor(waitTime.wait_minutes)} min
                </span>
              </div>
              {waitTime.charge > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  Wait charge: ${waitTime.charge.toFixed(2)} (after 1 min free)
                </p>
              )}
            </div>
          )}

          {/* Map with Mapbox or Leaflet */}
          {MAPBOX_TOKEN ? (
            <HexagonMap
              center={[ride.pickup_location.lng, ride.pickup_location.lat]}
              zoom={13}
              markers={mapMarkers}
              showSurgeZones={false}
              className="h-48"
            />
          ) : (
            <div className="h-40 rounded-xl overflow-hidden">
              <MapContainer 
                center={[ride.pickup_location.lat, ride.pickup_location.lng]} 
                zoom={13} 
                className="h-full w-full"
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[ride.pickup_location.lat, ride.pickup_location.lng]}>
                  <Popup>Pickup</Popup>
                </Marker>
                {ride.stops?.map((stop, i) => (
                  <Marker key={i} position={[stop.lat, stop.lng]}>
                    <Popup>Stop {i + 1}</Popup>
                  </Marker>
                ))}
                <Marker position={[ride.dropoff_location.lat, ride.dropoff_location.lng]}>
                  <Popup>Dropoff</Popup>
                </Marker>
              </MapContainer>
            </div>
          )}

          {/* Real-time Driver Location (for rider) */}
          {isRider && driverLocation && ride.status !== "pending" && (
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 text-blue-700">
                <Navigation className="w-4 h-4" />
                <span className="font-medium text-sm">Driver is {ride.status === "arrived" ? "waiting at pickup" : "on the way"}</span>
              </div>
            </div>
          )}

          {/* Locations */}
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span className="text-sm">{ride.pickup_location.address}</span>
            </div>
            {ride.stops?.map((stop, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${
                i < ride.current_stop_index ? "bg-slate-100" : "bg-yellow-50"
              }`}>
                <div className={`w-3 h-3 rounded-full ${
                  i < ride.current_stop_index ? "bg-slate-400" : "bg-yellow-500"
                }`} />
                <span className="text-sm">Stop {i + 1}: {stop.address}</span>
                {i < ride.current_stop_index && <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />}
              </div>
            ))}
            <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <span className="text-sm">{ride.dropoff_location.address}</span>
            </div>
          </div>

          {/* Note for Driver */}
          {ride.note_for_driver && (
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <StickyNote className="w-4 h-4" />
                <span className="font-medium text-sm">Note from rider</span>
              </div>
              <p className="text-sm text-slate-600">{ride.note_for_driver}</p>
            </div>
          )}

          {/* Driver/Rider Info */}
          {isRider ? (
            ride.driver_name && (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-[#1E4DB7] text-white">
                      {ride.driver_name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{ride.driver_name}</p>
                    <p className="text-sm text-slate-500">Your Driver</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => window.open(`tel:${ride.driver_phone}`)}
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setShowChat(true)}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback className="bg-[#1E4DB7] text-white">
                    {ride.rider_name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{ride.rider_name}</p>
                  <p className="text-sm text-slate-500">Rider • {ride.passenger_count || 1} passenger(s)</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => window.open(`tel:${ride.rider_phone}`)}
                >
                  <Phone className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="rounded-full"
                  onClick={() => setShowChat(true)}
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Fare */}
          <div className="p-4 bg-blue-50 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Fare</span>
              <span className="text-xl font-bold text-[#1E4DB7]">${ride.fare.toFixed(2)}</span>
            </div>
            {(ride.wait_time_charge > 0 || ride.cancellation_fee > 0) && (
              <div className="text-xs text-slate-500 space-y-1">
                {ride.base_fare && <div className="flex justify-between"><span>Base fare:</span><span>${ride.base_fare.toFixed(2)}</span></div>}
                {ride.wait_time_charge > 0 && <div className="flex justify-between text-orange-600"><span>Wait time:</span><span>+${ride.wait_time_charge.toFixed(2)}</span></div>}
                {ride.cancellation_fee > 0 && <div className="flex justify-between text-red-600"><span>Cancellation fee:</span><span>+${ride.cancellation_fee.toFixed(2)}</span></div>}
              </div>
            )}
          </div>

          {/* Ride PIN Display (for rider) */}
          {isRider && ride.ride_pin && ["pending", "accepted", "arrived"].includes(ride.status) && (
            <RiderPinDisplay pin={ride.ride_pin} />
          )}

          {/* Emergency SOS Button (for rider) */}
          {isRider && ["accepted", "arrived", "in_progress"].includes(ride.status) && (
            <PanicButton 
              rideId={ride.id} 
              token={localStorage.getItem('token')}
              onAlert={() => {}}
            />
          )}

          {/* Navigation Options (for driver) */}
          {!isRider && ["accepted", "arrived", "in_progress"].includes(ride.status) && (
            <div className="space-y-2">
              <Button
                data-testid="navigate-btn"
                variant="outline"
                onClick={() => setShowNavOptions(!showNavOptions)}
                className="w-full rounded-full"
              >
                <Compass className="w-4 h-4 mr-2" />
                Navigate to {ride.status === "in_progress" ? "Destination" : "Pickup"}
              </Button>
              {showNavOptions && (
                <NavigationOptions
                  destination={ride.status === "in_progress" ? ride.dropoff_location : ride.pickup_location}
                  origin={driverLocation}
                  onSelect={() => setShowNavOptions(false)}
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {isRider ? (
              <>
                {["pending", "accepted", "arrived"].includes(ride.status) && (
                  <Button
                    data-testid="cancel-ride-btn"
                    variant="outline"
                    onClick={onCancel}
                    className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50"
                  >
                    Cancel Ride {ride.status !== "pending" && "($5 fee)"}
                  </Button>
                )}
              </>
            ) : (
              <>
                {ride.status === "accepted" && (
                  <Button
                    data-testid="arrived-btn"
                    onClick={markArrived}
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                  >
                    <MapPin className="w-4 h-4 mr-2" /> I've Arrived
                  </Button>
                )}
                {ride.status === "arrived" && (
                  <>
                    {waitTime && (
                      <div className="p-3 bg-slate-100 rounded-xl text-center">
                        <p className="text-sm text-slate-600">
                          Waiting: <span className="font-bold">{Math.floor(waitTime.wait_minutes)} min</span>
                          {waitTime.charge > 0 && <span className="text-green-600"> (+${waitTime.charge.toFixed(2)})</span>}
                        </p>
                        {waitTime.can_cancel && (
                          <p className="text-xs text-orange-600 mt-1">Eligible for no-show cancellation</p>
                        )}
                      </div>
                    )}
                    <Button
                      data-testid="start-ride-btn"
                      onClick={startRide}
                      className="w-full bg-green-500 hover:bg-green-600 text-white rounded-full"
                    >
                      Start Ride
                    </Button>
                    {waitTime?.can_cancel && (
                      <Button
                        data-testid="no-show-cancel-btn"
                        variant="outline"
                        onClick={cancelRide}
                        className="w-full rounded-full border-orange-200 text-orange-600 hover:bg-orange-50"
                      >
                        Cancel (No-Show)
                      </Button>
                    )}
                  </>
                )}
                {ride.status === "in_progress" && (
                  <div className="flex flex-col w-full gap-2">
                    {ride.stops?.length > 0 && ride.current_stop_index < ride.stops.length && (
                      <Button
                        data-testid="next-stop-btn"
                        onClick={nextStop}
                        variant="outline"
                        className="w-full rounded-full"
                      >
                        <MapPinPlus className="w-4 h-4 mr-2" /> Complete Stop {ride.current_stop_index + 1}
                      </Button>
                    )}
                    <Button
                      data-testid="complete-ride-btn"
                      onClick={completeRide}
                      className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                    >
                      Complete Ride
                    </Button>
                  </div>
                )}
                {/* Driver can always cancel */}
                {["accepted", "arrived"].includes(ride.status) && !waitTime?.can_cancel && (
                  <Button
                    variant="ghost"
                    onClick={cancelRide}
                    className="w-full text-slate-500 hover:text-red-600"
                  >
                    Cancel Ride
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Chat Dialog */}
      <ChatDialog 
        open={showChat} 
        onOpenChange={setShowChat} 
        rideId={ride.id}
      />

      {/* Rating Dialog */}
      <RatingDialog 
        open={showRating} 
        onOpenChange={setShowRating} 
        ride={ride}
        onComplete={() => {
          if (isRider) setShowTip(true);
          onRefresh();
        }}
      />

      {/* Tip Dialog */}
      {isRider && (
        <TipDialog 
          open={showTip} 
          onOpenChange={setShowTip} 
          ride={ride}
          onComplete={onRefresh}
        />
      )}
    </>
  );
};

// Ride History Item
const RideHistoryItem = ({ ride }) => {
  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          ride.status === "completed" ? "bg-green-100" : "bg-slate-200"
        }`}>
          {ride.status === "completed" ? (
            <CheckCircle className="w-5 h-5 text-green-600" />
          ) : (
            <X className="w-5 h-5 text-slate-500" />
          )}
        </div>
        <div>
          <p className="font-medium text-sm">{ride.dropoff_location.address}</p>
          <p className="text-xs text-slate-500">
            {new Date(ride.created_at).toLocaleDateString()}
            {ride.stops?.length > 0 && ` • ${ride.stops.length} stops`}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold">${(ride.fare + ride.tip).toFixed(2)}</p>
        {ride.tip > 0 && (
          <p className="text-xs text-green-600">+${ride.tip.toFixed(2)} tip</p>
        )}
      </div>
    </div>
  );
};

// Chat Dialog
const ChatDialog = ({ open, onOpenChange, rideId }) => {
  const api = useApi();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");

  useEffect(() => {
    if (open) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [open, rideId]);

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/messages/${rideId}`);
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post("/messages", { ride_id: rideId, content: newMessage });
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#1E4DB7]" /> Chat
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-80 pr-4">
          <div className="space-y-3">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.sender_id === user?.id
                      ? "bg-[#1E4DB7] text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-900 rounded-bl-sm"
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <p className="text-xs mt-1 opacity-70">
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="flex gap-2 mt-4">
          <Input
            data-testid="chat-input"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="rounded-full"
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
          />
          <Button
            data-testid="send-message-btn"
            onClick={sendMessage}
            className="bg-[#1E4DB7] hover:bg-[#163A8F] rounded-full"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Rating Dialog
const RatingDialog = ({ open, onOpenChange, ride, onComplete }) => {
  const api = useApi();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  const submitRating = async () => {
    setLoading(true);
    try {
      await api.post("/ratings", { ride_id: ride.id, rating, comment });
      toast.success("Rating submitted!");
      onOpenChange(false);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit rating");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">Rate Your Ride</DialogTitle>
          <DialogDescription className="text-center">
            How was your experience?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <StarRating rating={rating} onRate={setRating} size="large" />
          <Textarea
            data-testid="rating-comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Leave a comment (optional)"
            className="rounded-xl"
          />
        </div>
        <DialogFooter>
          <Button
            data-testid="submit-rating-btn"
            onClick={submitRating}
            className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit Rating"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Tip Dialog with Tiers
const TipDialog = ({ open, onOpenChange, ride, onComplete }) => {
  const api = useApi();
  const [tip, setTip] = useState(5);
  const [selectedTier, setSelectedTier] = useState("tier1");
  const [loading, setLoading] = useState(false);

  const tipTiers = [
    { id: "tier1", amount: 5, label: "$5" },
    { id: "tier2", amount: 10, label: "$10" },
    { id: "tier3", amount: 15, label: "$15" },
    { id: "tier4", amount: 20, label: "$20" },
    { id: "custom", amount: null, label: "Custom" }
  ];

  const selectTier = (tier) => {
    setSelectedTier(tier.id);
    if (tier.amount) {
      setTip(tier.amount);
    }
  };

  const submitTip = async () => {
    setLoading(true);
    try {
      await api.post("/tips", { 
        ride_id: ride.id, 
        amount: tip,
        tier: selectedTier !== "custom" ? selectedTier : null
      });
      toast.success(`$${tip.toFixed(2)} tip added!`);
      onOpenChange(false);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add tip");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">Add a Tip</DialogTitle>
          <DialogDescription className="text-center">
            Show appreciation for your driver
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="flex flex-wrap justify-center gap-2">
            {tipTiers.map((tier) => (
              <Button
                key={tier.id}
                data-testid={`tip-tier-${tier.id}`}
                variant={selectedTier === tier.id ? "default" : "outline"}
                onClick={() => selectTier(tier)}
                className={`rounded-full min-w-[60px] ${
                  selectedTier === tier.id ? "bg-[#1E4DB7]" : ""
                }`}
              >
                {tier.label}
              </Button>
            ))}
          </div>
          {selectedTier === "custom" && (
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">$</span>
              <Input
                data-testid="custom-tip-input"
                type="number"
                min="1"
                max="500"
                value={tip}
                onChange={(e) => setTip(parseFloat(e.target.value) || 0)}
                className="text-center text-xl font-bold rounded-xl w-24"
              />
            </div>
          )}
          <div className="text-center">
            <p className="text-3xl font-bold text-[#1E4DB7]">${tip.toFixed(2)}</p>
            <p className="text-sm text-slate-500">Tip amount</p>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 rounded-full"
          >
            Skip
          </Button>
          <Button
            data-testid="submit-tip-btn"
            onClick={submitTip}
            className="flex-1 bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
            disabled={loading || tip < 1}
          >
            {loading ? "Adding..." : `Tip $${tip.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Driver Dashboard
const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const api = useApi();
  const [availableRides, setAvailableRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [showBankSetup, setShowBankSetup] = useState(false);
  const [hexagons, setHexagons] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [surgeInfo, setSurgeInfo] = useState(null);

  useEffect(() => {
    // Redirect if not fully onboarded
    if (user?.driver_status !== "active") {
      navigate("/driver/onboarding");
      return;
    }
    fetchData();
    getCurrentLocation();
    const interval = setInterval(fetchData, 5000);
    const locationInterval = setInterval(updateDriverLocation, 10000);
    return () => {
      clearInterval(interval);
      clearInterval(locationInterval);
    };
  }, [user]);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(loc);
          fetchHexagons(loc.lat, loc.lng);
          fetchSurgeInfo(loc.lat, loc.lng);
        },
        () => {
          // Default to NYC if geolocation fails
          setCurrentLocation({ lat: 40.7128, lng: -74.006 });
          fetchHexagons(40.7128, -74.006);
        }
      );
    }
  };

  const updateDriverLocation = async () => {
    if (currentLocation) {
      try {
        const response = await api.post(`/drivers/location?lat=${currentLocation.lat}&lng=${currentLocation.lng}&heading=0`);
        setSurgeInfo(response.data);
      } catch (error) {
        console.error("Failed to update location:", error);
      }
    }
  };

  const fetchHexagons = async (lat, lng) => {
    try {
      const response = await api.get(`/mapping/hexagons?lat=${lat}&lng=${lng}&radius_km=5`);
      setHexagons(response.data.hexagons);
    } catch (error) {
      console.error("Failed to fetch hexagons:", error);
    }
  };

  const fetchSurgeInfo = async (lat, lng) => {
    try {
      const response = await api.get(`/mapping/surge?lat=${lat}&lng=${lng}`);
      setSurgeInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch surge info:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [ridesRes, earningsRes] = await Promise.all([
        api.get("/rides"),
        api.get("/earnings")
      ]);
      
      const myRides = ridesRes.data.filter(r => r.driver_id === user?.id);
      const active = myRides.find(r => ["accepted", "arrived", "in_progress"].includes(r.status));
      setActiveRide(active || null);
      
      if (!active) {
        try {
          const available = await api.get("/rides/available");
          setAvailableRides(available.data);
        } catch (e) {
          setAvailableRides([]);
        }
      }
      
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };

  const acceptRide = async (rideId) => {
    try {
      await api.post(`/rides/${rideId}/accept`);
      toast.success("Ride accepted!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept ride");
    }
  };

  const requestInstantPay = async (amount) => {
    try {
      const response = await api.post("/instant-pay", { amount });
      toast.success(`Instant pay processed! Payout: $${response.data.payout.toFixed(2)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process instant pay");
    }
  };

  return (
    <div data-testid="driver-dashboard" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo size="small" />
          <div className="flex items-center gap-4">
            {surgeInfo?.is_boost_zone && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                <Zap className="w-4 h-4 mr-1" />
                Boost Zone
              </Badge>
            )}
            {surgeInfo?.surge_multiplier > 1 && (
              <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                {surgeInfo.surge_multiplier}x Surge
              </Badge>
            )}
            <Badge className="bg-green-100 text-green-700 border-green-200">
              <CircleDollarSign className="w-4 h-4 mr-1" />
              ${earnings?.pending_earnings?.toFixed(2) || "0.00"}
            </Badge>
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#1E4DB7] text-white">
                {user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="w-4 h-4 text-slate-600" />
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        {/* Driver Selfie Verification */}
        <div className="mb-6">
          <DriverSelfieVerification 
            token={localStorage.getItem('token')}
            onVerified={fetchData}
          />
        </div>

        {/* Surge Map with Hexagons */}
        {!activeRide && MAPBOX_TOKEN && hexagons.length > 0 && (
          <Card className="bg-white rounded-2xl shadow-sm mb-6 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
                <Map className="w-5 h-5 text-[#1E4DB7]" /> Demand Map
              </CardTitle>
              <CardDescription>Color-coded zones show current pricing. Blue = Boost+ earnings</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <HexagonMap
                center={currentLocation ? [currentLocation.lng, currentLocation.lat] : [-74.006, 40.7128]}
                zoom={12}
                hexagons={hexagons}
                showSurgeZones={true}
                className="h-64"
              />
            </CardContent>
          </Card>
        )}

        {/* Rating */}
        <Card className="bg-gradient-to-r from-[#1E4DB7] to-blue-600 text-white rounded-2xl shadow-lg mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Your Rating</p>
                <div className="flex items-center gap-2 mt-1">
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  <span className="text-3xl font-bold">{user?.rating?.toFixed(1) || "5.0"}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm">Total Rides</p>
                <p className="text-3xl font-bold">{user?.total_rides || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Ride */}
        {activeRide && (
          <ActiveRideCard 
            ride={activeRide} 
            onRefresh={fetchData}
            isRider={false}
          />
        )}

        {/* Available Rides */}
        {!activeRide && (
          <Card className="bg-white rounded-2xl shadow-sm mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
                <Navigation className="w-5 h-5 text-[#1E4DB7]" /> Available Rides
              </CardTitle>
            </CardHeader>
            <CardContent>
              {availableRides.length === 0 ? (
                <div className="text-center py-8">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No rides available right now</p>
                  <p className="text-sm text-slate-400">Check back soon!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableRides.map((ride) => (
                    <div key={ride.id} className="p-4 border border-slate-200 rounded-xl hover:border-[#1E4DB7] transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                            <span className="text-sm">{ride.pickup_location.address}</span>
                          </div>
                          {ride.stops?.map((stop, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full" />
                              <span className="text-sm text-slate-500">Stop: {stop.address}</span>
                            </div>
                          ))}
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full" />
                            <span className="text-sm">{ride.dropoff_location.address}</span>
                          </div>
                        </div>
                        <span className="font-bold text-lg text-[#1E4DB7]">${ride.fare.toFixed(2)}</span>
                      </div>
                      {ride.note_for_driver && (
                        <div className="mb-3 p-2 bg-amber-50 rounded-lg text-sm text-amber-700">
                          <StickyNote className="w-3 h-3 inline mr-1" />
                          {ride.note_for_driver}
                        </div>
                      )}
                      <Button
                        data-testid={`accept-ride-${ride.id}-btn`}
                        onClick={() => acceptRide(ride.id)}
                        className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
                      >
                        Accept Ride
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Earnings Card */}
        <Card className="bg-white rounded-2xl shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Plus_Jakarta_Sans']">
              <Wallet className="w-5 h-5 text-green-500" /> Earnings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <p className="text-sm text-slate-600">Pending</p>
                <p className="text-2xl font-bold text-green-600">
                  ${earnings?.pending_earnings?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600">Total Paid Out</p>
                <p className="text-2xl font-bold text-slate-900">
                  ${earnings?.total_earnings?.toFixed(2) || "0.00"}
                </p>
              </div>
            </div>

            {/* Bank Account */}
            {earnings?.bank_account ? (
              <div className="p-4 bg-blue-50 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BanknoteIcon className="w-5 h-5 text-[#1E4DB7]" />
                  <div>
                    <p className="font-medium">{earnings.bank_account.account_holder}</p>
                    <p className="text-sm text-slate-500">****{earnings.bank_account.account_number}</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700">Verified</Badge>
              </div>
            ) : (
              <Button
                data-testid="setup-bank-btn"
                onClick={() => setShowBankSetup(true)}
                variant="outline"
                className="w-full rounded-full border-dashed"
              >
                <Plus className="w-4 h-4 mr-2" /> Set Up Bank Account (Free)
              </Button>
            )}

            {/* Instant Pay */}
            {earnings?.bank_account && earnings?.pending_earnings > 2 && (
              <Button
                data-testid="instant-pay-btn"
                onClick={() => requestInstantPay(earnings.pending_earnings)}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white rounded-full"
              >
                <Zap className="w-4 h-4 mr-2" /> Instant Pay ($2.00 fee)
              </Button>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Bank Setup Dialog */}
      <BankSetupDialog 
        open={showBankSetup} 
        onOpenChange={setShowBankSetup}
        onComplete={fetchData}
      />

      <SafetyShield 
        onEmergency={() => toast.error("Calling 911...")}
        onCallOffice={() => toast.info("Calling DELEVR Office...")}
      />
    </div>
  );
};

// Bank Setup Dialog
const BankSetupDialog = ({ open, onOpenChange, onComplete }) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    account_holder: "",
    routing_number: "",
    account_number: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post("/bank-account", formData);
      toast.success("Bank account set up successfully!");
      onOpenChange(false);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set up bank account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set Up Bank Account</DialogTitle>
          <DialogDescription>
            Add your bank details for free direct deposits
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Account Holder Name</Label>
            <Input
              data-testid="bank-holder-input"
              value={formData.account_holder}
              onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
              placeholder="John Doe"
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label>Routing Number</Label>
            <Input
              data-testid="bank-routing-input"
              value={formData.routing_number}
              onChange={(e) => setFormData({ ...formData, routing_number: e.target.value })}
              placeholder="123456789"
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label>Account Number</Label>
            <Input
              data-testid="bank-account-input"
              value={formData.account_number}
              onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              placeholder="1234567890"
              className="rounded-xl"
            />
          </div>
          <Badge className="w-full justify-center bg-green-50 text-green-700 py-2">
            <CheckCircle className="w-4 h-4 mr-2" /> Free bank routing - no fees!
          </Badge>
        </div>
        <DialogFooter>
          <Button
            data-testid="submit-bank-btn"
            onClick={handleSubmit}
            className="w-full bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full"
            disabled={loading}
          >
            {loading ? "Setting up..." : "Set Up Bank Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Wallet Page
const WalletPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const api = useApi();
  const [loading, setLoading] = useState(false);

  const subscribeToVideo = async () => {
    setLoading(true);
    try {
      const response = await api.post("/payments/checkout", {
        package_type: "subscription",
        origin_url: window.location.origin
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronRight className="w-5 h-5 rotate-180" />
          </Button>
          <h1 className="font-semibold font-['Plus_Jakarta_Sans']">Wallet</h1>
        </div>
      </header>

      <main className="p-6 max-w-md mx-auto space-y-6">
        {/* Video Subscription */}
        <Card className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Video className="w-8 h-8" />
              <div>
                <h3 className="font-bold text-lg">Video Recording</h3>
                <p className="text-purple-100 text-sm">Record your rides for safety</p>
              </div>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <span className="text-3xl font-bold">$5</span>
                <span className="text-purple-200">/month</span>
              </div>
              {user?.video_subscription ? (
                <Badge className="bg-white/20 text-white">
                  <CheckCircle className="w-4 h-4 mr-1" /> Active
                </Badge>
              ) : null}
            </div>
          </div>
          <CardContent className="p-6">
            {user?.video_subscription ? (
              <div className="text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="font-medium">Subscription Active</p>
                <p className="text-sm text-slate-500">
                  Expires: {new Date(user.subscription_expires).toLocaleDateString()}
                </p>
              </div>
            ) : (
              <>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Record all rides automatically
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Cloud storage included
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Share with authorities if needed
                  </li>
                </ul>
                <Button
                  data-testid="subscribe-video-btn"
                  onClick={subscribeToVideo}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Subscribe Now"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card className="bg-white rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#1E4DB7]" /> Payment Methods
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 text-center py-4">
              Payment is processed securely via Stripe
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const api = useApi();
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-sm rounded-2xl shadow-lg text-center">
        <CardContent className="p-8">
          {status === "checking" && (
            <>
              <div className="w-16 h-16 border-4 border-[#1E4DB7] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Verifying payment...</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Payment Successful!</h2>
              <p className="text-slate-600 mb-6">Thank you for your purchase.</p>
              <Button
                onClick={() => navigate(user?.role === "driver" ? "/driver" : "/rider")}
                className="bg-[#1E4DB7] hover:bg-[#163A8F] text-white rounded-full px-8"
              >
                Continue
              </Button>
            </>
          )}
          {(status === "error" || status === "timeout" || status === "expired") && (
            <>
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Payment Issue</h2>
              <p className="text-slate-600 mb-6">
                {status === "expired" ? "Payment session expired." : "Unable to verify payment."}
              </p>
              <Button
                onClick={() => navigate("/wallet")}
                variant="outline"
                className="rounded-full px-8"
              >
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Teen Consent Form Page
const TeenConsentFormPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <img src="/logo.png" alt="DELEVR" className="h-12" />
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <X className="w-5 h-5 mr-2" /> Close
          </Button>
        </div>
        <TeenConsentForm onClose={() => navigate(-1)} />
      </div>
    </div>
  );
};

// Parent Dashboard Page
const ParentDashboardPage = () => {
  const { user, token } = useAuth();
  
  return <ParentDashboard token={token} user={user} />;
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1E4DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "driver" ? "/driver" : "/rider"} replace />;
  }

  return children;
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <CookieBanner />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/teen-consent" element={<TeenConsentFormPage />} />
          <Route
            path="/rider"
            element={
              <ProtectedRoute allowedRoles={["rider", "teen_rider"]}>
                <RiderDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/driver"
            element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/driver/onboarding"
            element={
              <ProtectedRoute allowedRoles={["driver"]}>
                <DriverOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-success"
            element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parent"
            element={
              <ProtectedRoute allowedRoles={["rider"]}>
                <ParentDashboardPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
